// What the two wipe screens share: the query keys they both read, the number
// and duration formats the mockup prints, and the clock the running states
// need.
//
// It is a module of its own because both readers — ProjectOverviewPage and
// WipeProjectDialog — are component modules, and Fast Refresh cannot
// hot-replace one that also exports a helper
// (react-refresh/only-export-components). Two copies of a duration format is
// how the same figure ends up written two ways on one screen.

import { useEffect, useState } from "react";

import type { ProjectWipeTask } from "./api";

/**
 * The project's latest wipe task. It sits under the project's own key, so
 * anything that invalidates the project takes the task with it — which is what
 * a finished wipe wants: every count on the page is wrong until it refetches.
 */
export function projectWipeQueryKey(projectId: string) {
  return ["project", projectId, "wipe"] as const;
}

/** The preflight the confirmation dialog reads. Same prefix rule. */
export function projectWipePreflightQueryKey(projectId: string) {
  return ["project", projectId, "wipe", "preflight"] as const;
}

/**
 * How long the wipe query may go unanswered before the page stops repeating
 * what it last heard.
 *
 * Seven or eight polls. Short enough that nobody watches a frozen percentage
 * for minutes, long enough that a single dropped request — a redeploy, a
 * proxy hiccup — does not rewrite a banner the operator is reading.
 */
export const WIPE_CONTACT_LOST_MS = 15_000;

/**
 * Whether the last thing this page heard about the wipe is old enough to stop
 * being reported as the present tense.
 *
 * Measured between the two timestamps react-query already keeps rather than by
 * counting failures: `failureCount` resets at the start of every fetch, so it
 * says how many RETRIES the current attempt has burned, never how many polls
 * in a row have come back empty. The gap between "last good answer" and "last
 * failure" is the quantity that actually matters — the age of the task object
 * every wiping decision on the page is derived from.
 *
 * Shaped to fit both readers unchanged: the `useQuery` result the page renders
 * from, and the `query.state` handed to `refetchInterval`.
 */
export function wipeContactLost(q: {
  error: unknown;
  dataUpdatedAt: number;
  errorUpdatedAt: number;
}): boolean {
  return (
    !!q.error && q.errorUpdatedAt - q.dataUpdatedAt >= WIPE_CONTACT_LOST_MS
  );
}

/**
 * What a wipe took, as the preflight counted it in the moment the operator was
 * asked to confirm.
 *
 * Neither the task nor anything left in the database can answer "how many
 * players went" afterwards — the rows are gone and the task reports rows per
 * step, not per entity. So the figures the dialog showed BEFORE the wipe are
 * the ones the report and the toast repeat: the best count anyone has, read a
 * moment before the deletion.
 *
 * Not a guarantee, and not offered as one. The preflight is fetched when the
 * dialog opens and the freeze only goes up when the POST lands, with the
 * operator typing a project name in between and the game API open the whole
 * time — a player who signed up in those seconds is wiped without being
 * counted. The alternative is to print no figure at all, which tells the
 * operator less about what they just destroyed than a number that is a few
 * players short.
 */
export interface WipeCounts {
  players: number;
  guilds: number;
}

const NUM = new Intl.NumberFormat("en-US");

/** "128,430" — the grouped figures of the mockup's `.num` and `.s-count`. */
export function num(n: number): string {
  return NUM.format(n);
}

/**
 * The noun for a count — "1 guild", "3,207 guilds".
 *
 * Same shape as DeletePlayerDialog's own `plural`: it returns the WORD, never
 * the number, because every figure on these two screens is wrapped in the
 * mockup's monospaced `.num` and the noun beside it is not. It lives here
 * rather than in either file because the dialog and the page print the same
 * sentence about the same project — "128,430 players and 3,207 guilds" — and
 * two copies of that rule is how one of them ends up saying "1 guilds".
 */
export function plural(n: number, word: string): string {
  return n === 1 ? word : `${word}s`;
}

/**
 * "1m 12s", "2m 08s", "48s" — the mockup's elapsed counters, with the
 * two-digit seconds it pads them to once there is a minute in front.
 */
export function formatDuration(ms: number): string {
  const total = Math.max(0, Math.round(ms / 1000));
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  if (minutes === 0) return `${seconds}s`;
  return `${minutes}m ${String(seconds).padStart(2, "0")}s`;
}

/**
 * `Date.now()`, refreshed every `intervalMs` while `live`.
 *
 * The elapsed counters are the one clock here that has to move on its own: the
 * task is polled every two seconds, and two polls that report the same numbers
 * are the same object after react-query's structural sharing, so nothing
 * re-renders and the counter would sit still on a step that takes a minute.
 *
 * The interval is a parameter because that is not the only thing on this page
 * whose text goes stale where nothing re-renders it: the Online band's «taken
 * N ago» ages by the minute rather than by the second (OnlineBand.tsx), and a
 * per-second tick for a per-minute sentence is 59 renders nobody reads.
 */
export function useNow(live: boolean, intervalMs = 1000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!live) return;
    const handle = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(handle);
  }, [live, intervalMs]);
  return now;
}

/**
 * How long the task has been running, or how long it ran. `now` is what
 * `useNow` returns — passed in rather than read here so a component renders
 * every counter it draws against one instant.
 */
export function taskElapsedMs(task: ProjectWipeTask, now: number): number {
  const from = Date.parse(task.started_at);
  if (Number.isNaN(from)) return 0;
  const finished = task.finished_at ? Date.parse(task.finished_at) : NaN;
  const to = Number.isNaN(finished) ? now : finished;
  return Math.max(0, to - from);
}
